import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCallIntelligencePLGV1Service, serviceName } from '../service';
import { GetSubscriptionBackfillStatusIO, ServiceQueries } from '../types';
import { useSchemaCallIntelligencePLGV1Queries } from './use-call-intelligence-v1-queries';
import {
  UseSchemaCallIntelligencePLGV1QueryEndpointArgs,
  useSchemaCallIntelligencePLGV1Query,
} from './use-call-intelligence-v1-query';

/**
 * A hook that returns a query for the `GetSubscriptionBackfillStatus` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetSubscriptionBackfillStatusQuery = <E = unknown, D = GetSubscriptionBackfillStatusIO['output']>(
  args: UseSchemaCallIntelligencePLGV1QueryEndpointArgs<'GetSubscriptionBackfillStatus', E, D>
) =>
  useSchemaCallIntelligencePLGV1Query({
    endpointName: 'GetSubscriptionBackfillStatus',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetSubscriptionBackfillStatus` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetSubscriptionBackfillStatusQueries = <E = unknown, D = GetSubscriptionBackfillStatusIO['output']>(
  argsArr: UseSchemaCallIntelligencePLGV1QueryEndpointArgs<'GetSubscriptionBackfillStatus', E, D>[]
) =>
  useSchemaCallIntelligencePLGV1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetSubscriptionBackfillStatus',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetSubscriptionBackfillStatus` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetSubscriptionBackfillStatus = () => {
  const queryClient = useQueryClient();

  const fetchGetSubscriptionBackfillStatus = async <E = unknown, D = GetSubscriptionBackfillStatusIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSchemaCallIntelligencePLGV1QueryEndpointArgs<'GetSubscriptionBackfillStatus', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetSubscriptionBackfillStatusIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetSubscriptionBackfillStatus'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetSubscriptionBackfillStatusIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetSubscriptionBackfillStatus'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetSubscriptionBackfillStatus'>({
          serviceName,
          endpointName: 'GetSubscriptionBackfillStatus',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCallIntelligencePLGV1Service.GetSubscriptionBackfillStatus(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetSubscriptionBackfillStatus;
};
